import { getCurrentLocale, useIcon } from '@clover-web-lib-sprout/common';
import { Icon, IconButton } from '@clover-web-lib-sprout/icon';
import cx from 'clsx';
import React, { forwardRef, useState } from 'react';
import { BANNER_ICON_MAP, Close } from './BannerIcons';
import { getLanguageStrings } from './localizations';
import styles from './styles.module.scss';

export type BannerVariant = 'info' | 'success' | 'warning' | 'error' | 'upsell';

export type AriaAttributes = { ariaLive: 'polite' | 'assertive' | 'off'; ariaAtomic: 'true' | 'false' };

export const ariaAttr: Record<BannerVariant, AriaAttributes> = {
  info: { ariaLive: 'polite', ariaAtomic: 'true' },
  success: { ariaLive: 'polite', ariaAtomic: 'true' },
  warning: { ariaLive: 'assertive', ariaAtomic: 'true' },
  error: { ariaLive: 'assertive', ariaAtomic: 'true' },
  upsell: { ariaLive: 'off', ariaAtomic: 'false' },
};

const ICON_MAP: Record<BannerVariant, string> = {
  info: 'Info',
  success: 'CheckCircle',
  warning: 'ColoredWarning',
  error: 'ErrorIcon',
  upsell: 'UpsellStar',
};

export type BannerProps = React.HTMLAttributes<HTMLDivElement> & {
  /** If the user can dismiss the banner */
  closeable?: boolean;
  /** Title text. Use sentence case. */
  title?: string;
  /** Type of banner to display. Defaults to 'info' */
  variant?: BannerVariant;
  /** Function to be called when banner closes */
  onCloseCallback?: () => void;
  /** Show or hide the banner */
  expose?: boolean;
  /** Defines the ARIA live attribute for accessibility */
  ariaLive?: AriaAttributes['ariaLive'];
  /** Defines the ARIA atomic attribute for accessibility */
  ariaAtomic?: AriaAttributes['ariaAtomic'];
  /** Selector for Cypress Tests and Heap Tracking */
  testId?: string;
  /** Data test id for the close button */
  closeBtnTestId?: string;
};

/**
 *
 * @title
 *  Clover styled banner to display information to user
 * @label
 *  Banner
 * @shortdescription
 *  Dismissable banner
 * @keywords
 *  [banner, info,error,warning,success,upsell, dismissable banner]
 * @description
 * Prepares a dismissable, banner-styled content area for surfacing critical information to the user.
 * @componentUsage
 *  <Banner variant="info" {...args}>
    <p>your summary content body goes here</p>
    </Banner>
 * @example
 *  <BannerExample args={{closeable : true , title : 'Banner demo'}} />
 *
 * <<BannerExample ariaLive="assertive" ariaAtomic="false" >/>
 */

export const Banner = forwardRef<HTMLDivElement, BannerProps>((props, ref) => {
  const {
    children,
    className,
    closeable = true,
    title,
    variant = 'info',
    onCloseCallback = () => {},
    expose = true,
    ariaLive = ariaAttr[variant].ariaLive,
    ariaAtomic = ariaAttr[variant].ariaAtomic,
    testId = 'banner',
    closeBtnTestId,
    ...bannerDivProps
  } = props;

  const text = getLanguageStrings(getCurrentLocale());

  const [exposed, setExposed] = useState(expose);
  const [fade, setFade] = useState(false);

  const iconGlyph = ICON_MAP[variant];

  const bannerIcon = iconGlyph || '';
  const variantIcon = useIcon(bannerIcon, BANNER_ICON_MAP);

  const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setFade(true);
    setTimeout(() => {
      setExposed(false);
    }, 200);
    if (onCloseCallback) {
      onCloseCallback();
    }
  };

  if (!exposed) return null;

  return (
    <div
      data-testid={testId}
      className={cx(styles.banner, styles[variant], className, fade && styles.fadeOut)}
      ref={ref}
      {...bannerDivProps}
      aria-live={ariaLive}
      aria-atomic={ariaAtomic}
      tabIndex={ref ? -1 : undefined}
    >
      <span className={styles.glyph}>
        <Icon IconSVG={variantIcon} aria-hidden="true" accessibleName={text[variant]} />
      </span>
      <div className={styles.container}>
        {title && (
          <p className={styles.update}>
            <strong className={styles.bannerTitle}>{title}</strong>
          </p>
        )}
        <p className={styles.content}>{children}</p>
      </div>
      {closeable && (
        <span className={styles.closeButtonContainer}>
          <IconButton
            className={styles.closeButton}
            onClick={onClick}
            IconSVG={Close}
            accessibleName={text['closeButton']}
            testId={closeBtnTestId}
          ></IconButton>
        </span>
      )}
    </div>
  );
});

Banner.displayName = 'Banner';
